import { useState } from 'react';
import { MdVisibility, MdVisibilityOff, MdCheckCircle } from 'react-icons/md';
import Input from '../../../../components/Input';
import FormLabel from '../../../../components/auth/FormLabel';
import { Button } from '../../../../components/Button';
import useUpdatePassworsSetup from '../api/useUpdatePassworsSetup';
import { Route } from '@/routes/agent_spa/account/setup.$id';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import useFetchSignupInfo from '../api/useFetchSignupInfo';

export default function PasswordSetupTemplate() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { id } = Route.useParams();
  const { mutateAsync: updatePassworsSetup } = useUpdatePassworsSetup(id);
  const { data: signupInfo } = useFetchSignupInfo(id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      setError('パスワードを入力してください');
      return;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await updatePassworsSetup({ password, password_confirmation: confirmPassword });
      setSuccess(true);
      // 1秒後にリダイレクト
      setTimeout(() => {
        if (signupInfo?.signup_mail_type === 'trial_registration') {
          window.location.href = '/agent/trial/tutorial';
        } else {
          window.location.href = '/agent/account/login';
        }
      }, 1000);
    } catch (error) {
      showErrorToast('パスワードの設定に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col items-stretch gap-2 overflow-hidden bg-gradient-to-b from-[rgba(129,139,154,0.89)] to-[#F4F6F9]">
      {/* 背景装飾画像 */}
      <div className="pointer-events-none absolute inset-0 size-full">
        <img
          src="/agent/trial/decoration-background.svg"
          alt=""
          className="size-full object-cover"
        />
      </div>

      {/* コンテンツエリア */}
      <div className="relative min-h-screen">
        <main className="flex min-h-screen flex-1 flex-col items-center justify-center max-md:pt-10">
          {/* ローディング画面 or 完了画面 */}
          {loading ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              {/* Loading Icon */}
              <div className="flex justify-center">
                <div
                  className="animate-spin rounded-full"
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid transparent',
                    borderTop: '4px solid #2563eb',
                    borderRight: '4px solid #2563eb',
                    borderBottom: '4px solid #2563eb',
                  }}
                ></div>
              </div>
              {/* Loading Text */}
              <p className="mt-[35px] text-base font-normal text-[#6D6D6D]">ログイン中...</p>
            </div>
          ) : success ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              {/* Success Icon */}
              <div className="flex justify-center">
                <MdCheckCircle className="text-[48px] text-primary" />
              </div>
              {/* Success Text */}
              <p className="mt-[35px] text-base font-bold text-primary">登録完了</p>
            </div>
          ) : (
            <div className="flex w-[480px] flex-col items-center max-md:w-full max-md:px-4 max-md:py-8">
              {/* タイトル */}
              <h1 className="text-3xl font-bold text-black">パスワード設定</h1>
              {/* 説明文 */}
              <p className="mt-x3l text-body-md text-black/70">
                今後のログインの際に必要となるパスワードを設定してください。
              </p>

              {/* メールアドレス表示 */}
              {signupInfo?.email && (
                <div className="mt-12 w-full">
                  <FormLabel>メールアドレス</FormLabel>
                  <div className="mt-2 text-sm font-normal text-black">{signupInfo.email}</div>
                </div>
              )}

              <form
                className="mt-8 flex w-full flex-col"
                onSubmit={handleSubmit}
                autoComplete="off"
              >
                <div>
                  <FormLabel required htmlFor="password">
                    パスワード
                  </FormLabel>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full"
                      autoComplete="new-password"
                    />
                    {showPassword ? (
                      <MdVisibility
                        className="absolute top-1/2 right-3 size-lg -translate-y-1/2 cursor-pointer text-sub"
                        onClick={() => setShowPassword((v) => !v)}
                      />
                    ) : (
                      <MdVisibilityOff
                        className="absolute top-1/2 right-3 size-lg -translate-y-1/2 cursor-pointer text-sub"
                        onClick={() => setShowPassword((v) => !v)}
                      />
                    )}
                  </div>
                </div>
                <div className="mt-8">
                  <FormLabel required htmlFor="confirmPassword">
                    確認パスワード
                  </FormLabel>
                  <div className="relative mt-2">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-white! px-3 py-[9px] text-base!"
                      autoComplete="new-password"
                    />
                    {showConfirmPassword ? (
                      <MdVisibility
                        className="absolute top-1/2 right-3 size-lg -translate-y-1/2 cursor-pointer text-sub"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                      />
                    ) : (
                      <MdVisibilityOff
                        className="absolute top-1/2 right-3 size-lg -translate-y-1/2 cursor-pointer text-sub"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                      />
                    )}
                  </div>
                </div>
                {error && <div className="mt-2 text-sm text-danger">{error}</div>}

                {/* 利用規約とプライバシーポリシーのリンク + 登録ボタン */}
                <div className="mt-x3l flex w-full flex-col items-center justify-center gap-md">
                  {/* リンクエリア */}
                  <div className="flex gap-md">
                    <a
                      href="https://help.agenthub.jp/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary-hover text-body-sm text-primary underline"
                    >
                      利用規約
                    </a>
                    <a
                      href="https://luumos.com/privacy_policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary-hover text-body-sm text-primary underline"
                    >
                      プライバシーポリシー
                    </a>
                  </div>

                  {/* 登録ボタン */}
                  <Button type="submit" color="primary" size="lg" className="h-[52px] w-[380px]">
                    同意し、登録する
                  </Button>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
